import { ChangeEvent, ReactNode, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FormProvider } from 'react-hook-form';

import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  EmptyState,
  IconButton,
  Input,
  Label,
  NumberInput,
  Patterns,
  TAccordionItem,
  Tabs,
  ITabItem,
  DateTools,
  Tooltip,
} from '@treezor/design-system';

import { CompanyMemberEmail } from '@/features/user/components/CreateUser/parts/business/companyMembers/CompanyMemberEmail';
import { CompanyMembersDocuments } from '@/features/user/components/CreateUser/parts/business/companyMembers/tabs/CompanyMembersDocuments';
import { SituationAndTaxResidence } from '@/features/user/components/CreateUser/parts/business/companyMembers/tabs/SituationAndTaxResidence';
import { BirthAndNationality } from '@/features/user/components/CreateUser/parts/business/companyMembers/tabs/BirthAndNationality';
import { EParentType } from '@/features/user/store/user.model';
import { defaultTaxResidence } from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { ICreateUserInfo } from '@/features/user/contexts/CreateUserContext';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { CreateUserInformation } from '@/features/user/components/CreateUser/parts/business/companyMembers/tabs/CreateUserInformation';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserCompanyMembers({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const {
    methods,
    user,
    members,
    language,
    register,
    watch,
    hasValidField,
    hasInvalidInputLength,
    handleChangeMemberProps,
    inputMaxNumber,
    handleBeforeSubmitMember,
    handleChangeMembers: handleChange,
    handleDeleteMember: handleDelete,
    onAddMember,
  } = useCreateBusinessUser({
    members: [],
  });

  const { isTaxResidenceDisabled } = useAddTaxResidences();

  const { isKycDocumentsInvalid } = useCreateUserKycDocuments();

  const [currentTabIndexes, setCurrentTabIndexes] = useState<number[]>(members?.map((_) => 0));

  const [currentRowOpen, setCurrentRowOpen] = useState<number>();

  const onRoleChange = useCallback(
    (memberIndex: number, role: EParentType, isChecked: boolean) => {
      const memberWatch = watch(`members.${memberIndex}`);

      const currentRoles = memberWatch.roles;
      const newRoles = isChecked ? [...currentRoles, role] : currentRoles.filter((r) => r !== role);

      const currentTaxResidences = memberWatch.taxResidences;

      let parentType = '';
      let controllingPersonType = undefined;
      let taxResidences = currentTaxResidences;

      if (newRoles?.length && !newRoles.includes(EParentType.leader) && !currentTaxResidences?.length) {
        taxResidences = [{ ...defaultTaxResidence, isDirty: true }];
      }

      if (
        (!newRoles?.length || newRoles.includes(EParentType.leader)) &&
        currentTaxResidences?.length === 1 &&
        !!currentTaxResidences[0]?.isDirty &&
        !currentTaxResidences[0]?.country
      ) {
        taxResidences = [];
      }

      if (newRoles.includes(EParentType.employee)) {
        parentType = EParentType.employee;
      } else if (newRoles.includes(EParentType.shareholder) && newRoles.includes(EParentType.leader)) {
        parentType = EParentType.leader;
        controllingPersonType = 3;
      } else {
        parentType = newRoles[0];
        controllingPersonType = newRoles.includes(EParentType.shareholder) ? 1 : 3;
      }

      handleChangeMemberProps(memberIndex, [
        { key: 'roles', value: newRoles },
        { key: 'parentType', value: parentType },
        { key: 'controllingPersonType', value: controllingPersonType },
        { key: 'taxResidences', value: taxResidences },
      ]);
    },
    [handleChangeMemberProps, watch],
  );

  const onHandleAddMember = useCallback(() => {
    setCurrentRowOpen(members.length);
    onAddMember();
    setCurrentTabIndexes([...currentTabIndexes, 0]);
  }, [currentTabIndexes, members.length, onAddMember]);

  const onHandleDeleteMember = useCallback(
    (memberIndex: number) => {
      handleDelete(memberIndex);
      const newCurrentTabIndexes = currentTabIndexes.filter((_, i) => i !== memberIndex);
      setCurrentTabIndexes(newCurrentTabIndexes);
    },
    [currentTabIndexes, handleDelete],
  );

  const onTabChange = useCallback(
    (tabIndex: number, memberIndex: number) => {
      const newCurrentTabIndexes = [...currentTabIndexes];
      newCurrentTabIndexes[memberIndex] = tabIndex;
      setCurrentTabIndexes(newCurrentTabIndexes);
    },
    [currentTabIndexes],
  );

  const isEmailAlreadyUsed = useCallback(
    (email: string, memberIndex: number) =>
      members.some((member, index) => {
        if (index < memberIndex && member.email) {
          return member.email === email;
        }
        return false;
      }) || email === user.email,
    [members, user.email],
  );

  const isUserInfoInvalid = useCallback(
    (memberWatch: ICreateUserInfo) => {
      return (
        !memberWatch?.firstname ||
        !memberWatch?.lastname ||
        !memberWatch?.email ||
        !memberWatch?.parentType ||
        !!inputMaxNumber(memberWatch.effectiveBeneficiary, 100)?.error ||
        hasInvalidInputLength([`${memberWatch}.address1`, `${memberWatch}.address2`, `${memberWatch}.address3`]) ||
        hasInvalidInputLength(['secondaryAddress1', 'secondaryAddress2', 'secondaryAddress3']) ||
        (!!memberWatch.mobile && !Patterns.phone.test(memberWatch.mobile)) ||
        (!!memberWatch.phone && !Patterns.phone.test(memberWatch.phone))
      );
    },
    [hasInvalidInputLength, inputMaxNumber],
  );

  const isBirthAndNationalityInvalid = useCallback(
    (memberWatch: ICreateUserInfo) => {
      const selectedDate = DateTools.localStringToDate(memberWatch.birthday, language);
      return !!selectedDate && DateTools.compare(new Date(), selectedDate) === 1;
    },
    [language],
  );

  const isFormDisabled = useMemo(() => {
    return (
      !!methods.formState.errors?.['members'] ||
      (!!members?.length
        ? members?.some((_, memberIndex) => {
            const memberWatch = watch(`members.${memberIndex}`);
            return (
              isUserInfoInvalid(memberWatch) ||
              isBirthAndNationalityInvalid(memberWatch) ||
              isTaxResidenceDisabled(memberWatch.taxResidences) ||
              isKycDocumentsInvalid(memberWatch.kycDocuments)
            );
          })
        : false)
    );
  }, [
    isBirthAndNationalityInvalid,
    isKycDocumentsInvalid,
    isTaxResidenceDisabled,
    isUserInfoInvalid,
    members,
    watch,
    methods.formState?.errors,
  ]);

  const columns: { label: ReactNode; field: string }[] = useMemo(
    () => [
      {
        label: (
          <Label isRequired>
            {t('pages.dashboard.createUser.form.lastname')} ({members?.length})
          </Label>
        ),
        field: 'lastname',
      },
      {
        label: <Label isRequired>{t('pages.dashboard.createUser.form.firstname')}</Label>,
        field: 'firstname',
      },
      {
        label: <Label isRequired>{t('pages.dashboard.createUser.form.email')}</Label>,
        field: 'email',
      },
    ],
    [members?.length, t],
  );

  const tabItems = useCallback(
    (memberIndex) => {
      const memberWatch = watch(`members.${memberIndex}`);
      return [
        {
          label: t('pages.dashboard.createUser.form.pages.userInformation'),
          element: (
            <CreateUserInformation
              memberIndex={memberIndex}
              memberWatch={memberWatch}
              handleChange={handleChange}
              hasValidField={hasValidField}
              register={register}
            />
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.birthNationality'),
          element: (
            <BirthAndNationality
              memberIndex={memberIndex}
              memberWatch={memberWatch}
              handleChange={handleChange}
              register={register}
            />
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.situationAndTaxResidence'),
          element: (
            <SituationAndTaxResidence
              memberIndex={memberIndex}
              memberWatch={memberWatch}
              handleChange={handleChange}
              handleChangeMemberProps={handleChangeMemberProps}
            />
          ),
        },
        {
          label: t('pages.dashboard.createUser.form.blocks.documents'),
          element: (
            <CompanyMembersDocuments memberIndex={memberIndex} memberWatch={memberWatch} handleChange={handleChange} />
          ),
        },
      ] as ITabItem[];
    },
    [handleChange, handleChangeMemberProps, hasValidField, register, t, watch],
  );

  const memberRows: TAccordionItem[] = useMemo(
    () =>
      members?.map((_, memberIndex) => {
        const memberKey = `members.${memberIndex}` as const;
        return {
          header: (
            <div className="flex flex-col gap-3 w-full">
              <div className="flex gap-3 w-full" key={`header-container_${memberIndex}`}>
                <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 w-full" key={`header-${memberIndex}`}>
                  <Input
                    label=""
                    type="text"
                    {...register(`${memberKey}.lastname`)}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleChange(memberIndex, 'lastname', event.currentTarget.value)
                    }
                  />
                  <Input
                    type="text"
                    label=""
                    {...register(`${memberKey}.firstname`)}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleChange(memberIndex, 'firstname', event.currentTarget.value)
                    }
                  />
                  <CompanyMemberEmail
                    memberKey={`${memberKey}.email`}
                    isEmailNotAlreadyUsed={(email: string) => !isEmailAlreadyUsed(email, memberIndex)}
                  />
                </div>
                <IconButton
                  icon="delete"
                  onClick={() => onHandleDeleteMember(memberIndex)}
                  variant="secondary"
                  key={`delete-btn-${memberIndex}`}
                />
              </div>
              <div className="flex gap-3 " key={`subheader-${memberIndex}`}>
                <Label isRequired className="typo-body-medium-paragraph mt-3">
                  {t('pages.dashboard.createUser.form.role')}
                </Label>
                <Checkbox
                  label={t('pages.dashboard.createUser.form.roles.leader')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onRoleChange(memberIndex, EParentType.leader, event?.target?.checked)
                  }
                  checked={watch(`${memberKey}.roles`).includes(EParentType.leader)}
                  className="mt-1"
                  disabled={watch(`${memberKey}.roles`).includes(EParentType.employee)}
                />
                <div className="flex gap-3">
                  <Checkbox
                    label={t('pages.dashboard.createUser.form.roles.shareholderHolding')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onRoleChange(memberIndex, EParentType.shareholder, event?.target?.checked)
                    }
                    checked={watch(`${memberKey}.roles`).includes(EParentType.shareholder)}
                    className="mt-1"
                    disabled={watch(`${memberKey}.roles`).includes(EParentType.employee)}
                  />
                  <NumberInput
                    label=""
                    defaultValue={watch(`${memberKey}.effectiveBeneficiary`)}
                    placeholder="..."
                    inputProps={{
                      ...register(`${memberKey}.effectiveBeneficiary`),
                    }}
                    unit={
                      <div
                        className={twMerge(
                          'flex gap-2 items-center h-[20px]',
                          !watch(`${memberKey}.roles`).includes(EParentType.shareholder) && 'text-disabled',
                        )}
                      >
                        <Divider direction="vertical" />
                        <div>%</div>
                      </div>
                    }
                    size="s"
                    className="!w-[22px]"
                    disabled={!watch(`${memberKey}.roles`).includes(EParentType.shareholder)}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleChange(memberIndex, 'effectiveBeneficiary', event.currentTarget.value)
                    }
                    {...inputMaxNumber(watch(`${memberKey}.effectiveBeneficiary`), 100)}
                  />
                </div>
                <Checkbox
                  label={t('pages.dashboard.createUser.form.roles.employee')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onRoleChange(memberIndex, EParentType.employee, event?.target?.checked)
                  }
                  checked={watch(`${memberKey}.roles`).includes(EParentType.employee)}
                  className="mt-1"
                  disabled={
                    watch(`${memberKey}.roles`).includes(EParentType.shareholder) ||
                    watch(`${memberKey}.roles`).includes(EParentType.leader)
                  }
                />
              </div>
            </div>
          ),
          children: (
            <Tabs
              items={tabItems(memberIndex)}
              size="s"
              onTabChanged={(tabIndex) => onTabChange(tabIndex, memberIndex)}
              currentTabIndex={currentTabIndexes[memberIndex]}
              className="overflow-auto"
            />
          ),
          isDefaultExpanded: memberIndex === currentRowOpen,
        };
      }),
    [
      members,
      register,
      isEmailAlreadyUsed,
      watch,
      t,
      inputMaxNumber,
      tabItems,
      currentTabIndexes,
      currentRowOpen,
      handleChange,
      onHandleDeleteMember,
      onRoleChange,
      onTabChange,
    ],
  );

  return (
    <FormProvider {...methods}>
      {members.length === 0 && (
        <div className="w-full min-h-[calc(100vh_-_400px)] flex items-center justify-center">
          <EmptyState
            action={t('pages.dashboard.createUser.business.steps.members.add')}
            icon="organigram_child"
            onClick={onHandleAddMember}
            state={t('pages.dashboard.createUser.business.steps.members.noMembers.description')}
            title={t('pages.dashboard.createUser.business.steps.members.noMembers.title')}
            size="l"
          />
        </div>
      )}
      {members.length !== 0 && (
        <div className="flex flex-col gap-7 min-h-[calc(100vh_-_400px)] pb-4" key={`members_container`}>
          <div className="flex justify-between">
            <p className="typo-h5-bold">{t('pages.dashboard.createUser.form.pages.companyMembers')}</p>
            {members?.length >= 10 ? (
              <Tooltip
                messageContent={t('pages.dashboard.createUser.form.actions.addCompanyMemberDisabled')}
                contentClassName="w-[200px]"
              >
                <Button variant="tertiary" icon="add" className="ml-auto" disabled>
                  {t('pages.dashboard.createUser.business.steps.members.add')}
                </Button>
              </Tooltip>
            ) : (
              <Button variant="tertiary" icon="add" className="ml-auto" onClick={onHandleAddMember}>
                {t('pages.dashboard.createUser.business.steps.members.add')}
              </Button>
            )}
          </div>
          <div>
            <div className="flex bg-secondary border-primary border-b-2">
              <div key="cell_icon" className="w-[44px]"></div>
              <div className="flex-1 grid grid-cols-[1fr_1fr_1fr]">
                {columns.map(({ field, label }) => (
                  <div
                    key={`cell_${field}`}
                    className="text-left whitespace-pre-wrap px-4 pt-3.5 pb-3 text-secondary typo-body-medium-small"
                  >
                    {label}
                  </div>
                ))}
              </div>
              <div key="cell_action" className="w-[40px]"></div>
            </div>
            <Accordion iconPosition="left" accordionContent={memberRows} className="!overflow-visible" />
          </div>
        </div>
      )}
      <CreateUserFooter
        isNextDisabled={isFormDisabled}
        onNext={handleBeforeSubmitMember}
        onPrev={handleBeforeSubmitMember}
        stepsLength={stepsLength}
      />
    </FormProvider>
  );
}
